import React, { useCallback } from 'react';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { WizardFooter } from '@/shared/components/Wizard.tsx';
import { Button } from '@schuettflix/planum-react';
import { useTranslation } from 'react-i18next';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { FormProvider } from 'react-hook-form';
import { getFinanceSettingsFormSchema } from '@/shared/forms/FinanceSettingsForm/schema/getFinanceSettingsFormSchema.ts';
import { UpsertFinanceSettings } from '@/shared/models/ContextDataModel/ContextData.ts';
import { isNotTransportExchangeOrganization } from '@/shared/forms/FinanceSettingsForm/utils/isNotTransportExchangeOrganization.ts';
import { addOrganizationsOffsets } from '@/shared/forms/FinanceSettingsForm/utils/addOrganizationsOffsets.ts';
import { useFinanceSettingsOrganizations } from '@/shared/forms/FinanceSettingsForm/hooks/useFinanceSettingsOrganizations.ts';
import { FinanceSettingsOrg } from '@/shared/forms/FinanceSettingsForm/components/FinanceSettingsOrg.tsx';
import { FinanceSettingsTransportExchange } from '@/shared/forms/FinanceSettingsForm/components/FinanceSettingsTransportExchange.tsx';

type FinanceSettingsFormProps = {
    customerOrganizationId: number;
    productConfigurations: ProductConfiguration[];
    isSubmissionPending: boolean;
    isSubmissionSuccessful: boolean;
    onSubmit: (financeSettings: FinanceSettings[]) => void;
    submitButtonLabel: string;
    submitButtonDataTest?: string;
    defaultValues?: FinanceSettings[];
    mode: 'create' | 'edit';
    onChange?: (financeSettings: UpsertFinanceSettings) => void;
};

export const FinanceSettingsForm: React.FC<FinanceSettingsFormProps> = ({
    customerOrganizationId,
    productConfigurations,
    isSubmissionPending,
    isSubmissionSuccessful,
    onSubmit,
    onChange,
    submitButtonLabel,
    submitButtonDataTest,
    defaultValues,
    mode,
}) => {
    const { t } = useTranslation();
    const organizations = useFinanceSettingsOrganizations(productConfigurations, customerOrganizationId);

    const financeSettingsOrganizations = addOrganizationsOffsets(
        organizations.filter(isNotTransportExchangeOrganization)
    );
    const transportExchangeOrganization = organizations.find(org => !isNotTransportExchangeOrganization(org));

    const methods = useZodForm({
        schema: getFinanceSettingsFormSchema({
            errorMessage: t('forms.merchantForm.errorMessages.required'),
            billingTextMaxLengthErrorMessage: t('forms.financeSettings.errorMessages.billingText.maxLength'),
        }),
    });

    const { handleSubmit } = methods;

    const handleFormSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
        async event => {
            event.preventDefault();
            await handleSubmit(({ financeSettings }) => {
                return onSubmit(financeSettings);
            }, console.error)();
        },
        [handleSubmit, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <form
                className="@container/summary flex flex-1 flex-col justify-between"
                onSubmit={handleFormSubmit}
                data-test="finance-settings-form"
            >
                <div className="my-6 flex flex-col gap-6 [&>article:first-child]:border-b">
                    {financeSettingsOrganizations.map(organization => (
                        <FinanceSettingsOrg
                            key={organization.organizationId}
                            organization={organization}
                            defaultValues={defaultValues}
                            onChange={onChange}
                        />
                    ))}
                    {transportExchangeOrganization && (
                        <FinanceSettingsTransportExchange
                            key={transportExchangeOrganization.organizationId}
                            organization={transportExchangeOrganization}
                        />
                    )}
                </div>
                <WizardFooter>
                    <Button
                        loading={isSubmissionPending || isSubmissionSuccessful}
                        type="submit"
                        data-test={submitButtonDataTest}
                        disabled={isSubmissionPending || isSubmissionSuccessful}
                        name="position-submit-button"
                        variant={mode === 'create' ? 'primary' : 'secondary'}
                    >
                        {submitButtonLabel}
                    </Button>
                </WizardFooter>
            </form>
        </FormProvider>
    );
};
